import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { leadDate } from "../services/leadsService";
import {
  fetchActivities,
  type Activity,
  type ActivityType,
} from "../services/activityService";
import { countsInWorkHours, isSystemActivity } from "../services/activityInsights";
import ActividadComercialCharts from "../components/ActividadComercialCharts";

// ===========================================================================
// Actividad — LIVE timeline of the whole CRM account. Reads real events from
// the API (`/crm/activitiesList`), written server-side by the CRM triggers:
//   • a lead is created (landing / API)
//   • a lead changes status
//   • an AI/human call is placed
//   • a call resolves (completed / failed / no answer ...)
//   • a meeting with a prospect counted as work hours (#1308)
// Polls every 15s so new events surface shortly after they happen.
//
// (#1309) Por omisión se esconde lo que hace el SISTEMA (leads que llegan del
// landing, llamadas de la IA, sincronizaciones): el panel muestra la actividad
// real del equipo comercial, con un contador de lo oculto y un interruptor para
// verlo todo. Arriba, las gráficas de tiempo por prospecto.
// ===========================================================================

const POLL_MS = 15000;

const TYPE_META: Record<ActivityType, { label: string; icon: string; tone: string }> = {
  lead: { label: "Lead", icon: "ri-user-add-line", tone: "bg-sky-500/15 text-sky-500" },
  call: { label: "Llamada", icon: "ri-phone-line", tone: "bg-primary-500/15 text-primary-500" },
  pipeline: { label: "Pipeline", icon: "ri-flow-chart", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  meeting: { label: "Reunión", icon: "ri-calendar-check-line", tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  other: { label: "Evento", icon: "ri-history-line", tone: "bg-gray-400/15 text-gray-500" },
};

const TYPE_ORDER: ActivityType[] = ["meeting", "call", "lead", "pipeline"];

function fmtDate(value: Activity["createdAt"]): string {
  const d = leadDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Lo que se pinta según el interruptor: con «solo equipo» fuera lo del sistema.
 * Puro, para probarse solo.
 */
export function visibleActivities(activities: Activity[], { teamOnly, typeFilter, query }: { teamOnly: boolean; typeFilter: ActivityType | "all"; query: string }): { rows: Activity[]; hiddenSystem: number } {
  const q = query.trim().toLowerCase();
  let hiddenSystem = 0;
  const rows = activities.filter((a) => {
    if (teamOnly && isSystemActivity(a)) {
      hiddenSystem += 1;
      return false;
    }
    if (typeFilter !== "all" && a.type !== typeFilter) return false;
    if (!q) return true;
    return [a.description, a.entityName, a.actor].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });
  return { rows, hiddenSystem };
}

export default function ActividadLive() {
  // `isDarkMode` sólo lo necesitan las gráficas (recharts); el resto usa tokens.
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<ActivityType | "all">("all");
  // #1309: por omisión solo la actividad real del equipo comercial.
  const [teamOnly, setTeamOnly] = useState(true);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    setError(null);
    try {
      const page = await fetchActivities({ limit: 200 });
      setActivities(page.data);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      // A 404 means the activity endpoint isn't available yet (e.g. functions
      // not deployed) — there's simply no activity to show, so fall back to the
      // empty state instead of surfacing a scary error code.
      if (err.response?.status === 404) {
        setActivities([]);
      } else {
        setError(err.response?.data?.message || err.message || "No se pudo cargar la actividad");
      }
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Lo que queda tras el interruptor «solo equipo»: los chips cuentan sobre eso.
  const base = useMemo(() => visibleActivities(activities, { teamOnly, typeFilter: "all", query: "" }), [activities, teamOnly]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: base.rows.length };
    for (const t of TYPE_ORDER) c[t] = 0;
    for (const a of base.rows) c[a.type] = (c[a.type] ?? 0) + 1;
    return c;
  }, [base.rows]);

  const filtered = useMemo(() => visibleActivities(activities, { teamOnly, typeFilter, query }).rows, [activities, teamOnly, typeFilter, query]);

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-history-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Actividad</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {teamOnly ? "Lo que hizo el equipo comercial, en vivo" : "Línea de tiempo en vivo de toda la cuenta"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar actividad..."
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
              <span className="whitespace-nowrap">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Gráficas de tiempo por prospecto (#1309) */}
        <ActividadComercialCharts isDarkMode={isDarkMode} />

        {/* Interruptor «solo equipo» + chips por tipo */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <label className={`mr-2 flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`} data-testid="team-only-toggle">
            <input
              type="checkbox"
              checked={teamOnly}
              onChange={(e) => setTeamOnly(e.target.checked)}
              className="h-4 w-4 accent-primary-500"
              aria-label="Solo actividad del equipo comercial"
            />
            <span>Solo actividad del equipo comercial</span>
            {teamOnly && base.hiddenSystem > 0 && (
              <span className={`rounded-full bg-gray-400/15 px-2 py-0.5 text-xs ${tc.textSecondary}`} data-testid="hidden-system-count" title="Eventos del sistema ocultos: leads del landing, llamadas de la IA, sincronizaciones">
                {base.hiddenSystem} del sistema ocultos
              </span>
            )}
          </label>
          <Chip
            active={typeFilter === "all"}
            label="Todo"
            count={counts.all}
            onClick={() => setTypeFilter("all")}
          />
          {TYPE_ORDER.map((t) => (
            <Chip
              key={t}
              active={typeFilter === t}
              label={TYPE_META[t].label}
              count={counts[t] ?? 0}
              tone={TYPE_META[t].tone}
              onClick={() => setTypeFilter(t)}
            />
          ))}
        </div>

        {/* Timeline */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} data-testid="activity-timeline">
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando actividad...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-inbox-line mb-2 block text-3xl" />
              {teamOnly && base.hiddenSystem > 0
                ? "El equipo comercial no tiene actividad registrada todavía. Las reuniones con prospectos y las llamadas aparecerán aquí; desmarca el filtro para ver los eventos del sistema."
                : "Aún no hay actividad. Los eventos (leads, cambios de estado, llamadas y reuniones) aparecerán aquí en cuanto ocurran."}
            </div>
          ) : (
            <ul className="divide-y divide-line/60">
              {filtered.map((a) => {
                const meta = TYPE_META[a.type] ?? TYPE_META.other;
                const inHours = countsInWorkHours(a);
                const minutes = Number((a.meta as Record<string, unknown> | undefined)?.minutes) || 0;
                return (
                  <li key={a.id} className="flex items-start gap-3 p-4" data-testid={`activity-${a.id}`}>
                    <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
                      <i className={`${meta.icon} text-base`} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.tone}`}>
                          {meta.label}
                        </span>
                        <p className={`text-sm font-medium ${tc.text}`}>{a.description}</p>
                        {inHours && (
                          <Link
                            to="/work-hours/time-entries"
                            className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-medium text-violet-600 hover:underline dark:text-violet-300"
                            title="Esta reunión ya suma en Horas Trabajadas del comercial"
                            data-testid={`counts-in-hours-${a.id}`}
                          >
                            <i className="ri-time-line mr-1" />
                            Cuenta en Horas Trabajadas{minutes ? ` · ${minutes} min` : ""}
                          </Link>
                        )}
                      </div>
                      <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>
                        {a.entityName && (
                          <>
                            <i className="ri-links-line mr-1" />
                            {a.entityName}
                            <span className="mx-1.5">·</span>
                          </>
                        )}
                        {a.actor || "Sistema"}
                      </p>
                    </div>
                    <span className={`whitespace-nowrap text-xs ${tc.textSecondary}`}>
                      {fmtDate(a.createdAt)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Datos en vivo. La línea de tiempo se actualiza automáticamente cada 15 segundos.
          {teamOnly && " Los eventos del sistema (leads del landing, llamadas de la IA, sincronizaciones) están ocultos."}
        </p>
      </div>
    </div>
  );
}

function Chip({
  active,
  label,
  count,
  tone,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  tone?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-primary-500 bg-primary-500 text-white"
          : "border-line bg-card text-content-muted hover:border-primary-500"
      }`}
    >
      <span>{label}</span>
      <span
        className={`rounded-full px-2 py-0.5 text-xs ${
          active ? "bg-white/20 text-white" : tone ?? "bg-gray-400/15 text-gray-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
